import { z } from 'zod/v4';

/**
 * Classification of a single hybrid-search candidate, relative to the brand's
 * network and any amfori site id already linked to that candidate:
 *
 * - `eligible` — in the brand's network AND has no conflicting amfori site id
 *   (its stored site id is unset, or already equals the site being synced).
 * - `blocked` — in the brand's network BUT already linked to a DIFFERENT amfori
 *   site id, so re-linking it to this site would clobber an existing mapping.
 * - `out_of_network` — not in the brand's network at all.
 */
export const CandidateClassificationSchema = z.enum([
  'eligible',
  'blocked',
  'out_of_network',
]);

export type CandidateClassification = z.infer<
  typeof CandidateClassificationSchema
>;

/**
 * A candidate the matcher considered during its hybrid-search step, captured so
 * step-logs can explain WHY an outcome was reached (e.g. which two companies
 * were too close to call, or which in-network company was blocked).
 *
 * Carries only the fields needed to identify and explain the candidate. By
 * design it carries `country` but NOT city/postal — the matcher does not key
 * off finer location data.
 */
export const ConsideredCandidateSchema = z.object({
  companyId: z.string(),
  companyName: z.string().nullable(),
  vectorSimilarity: z.number(),
  stringSimilarity: z.number(),
  classification: CandidateClassificationSchema,
  /** The amfori site id already linked to this candidate, when any. */
  existingAmforiSiteId: z.string().optional(),
  country: z.string().nullable().optional(),
});

export type ConsideredCandidate = z.infer<typeof ConsideredCandidateSchema>;

/**
 * Reason an amfori site could not be resolved to a single Retraced company:
 *
 * - `AMBIGUOUS` — two or more eligible candidates scored within the ambiguity
 *   window of the top one; too close to auto-pick.
 * - `BLOCKED` — the only in-network candidates above threshold are blocked
 *   (already linked to a different amfori site id).
 * - `OUT_OF_NETWORK` — defensive/unused: out-of-network suggestions are
 *   surfaced as `SUGGESTED_OUT_OF_NETWORK`, the canonical out-of-network path.
 * - `NO_CANDIDATES` — nothing matched any identifier and no name candidate
 *   cleared the confidence threshold.
 * - `RETIRED_SITE` — the site has no monitorings in any initiative and is not
 *   already linked to a supplier. amfori cannot delete a site, so members retire
 *   one by renaming it; such a record has no audit data to bring in and must not
 *   be fuzzy-matched into a supplier link.
 */
export const UnmatchedReasonSchema = z.enum([
  'AMBIGUOUS',
  'BLOCKED',
  'OUT_OF_NETWORK',
  'NO_CANDIDATES',
  'RETIRED_SITE',
]);

export type UnmatchedReason = z.infer<typeof UnmatchedReasonSchema>;

/**
 * Result of resolving an amfori sync payload (a site + its business partner)
 * to a Retraced company. Produced by `AmforiSupplierMatcherService.match()`.
 *
 * Three mutually-exclusive outcomes, discriminated on `status`:
 *
 * - `MATCHED` — resolved to a single supplier. Either a deterministic
 *   identifier match (`amfori_site_id` / `external_reference`) or a confident
 *   `hybrid_search` name match. When matched via hybrid search the considered
 *   candidates are attached for auditability.
 * - `SUGGESTED_OUT_OF_NETWORK` — a fuzzy name match that clears the threshold
 *   but is NOT in the brand's network (caller decides whether to link). This is
 *   the canonical out-of-network path.
 * - `UNMATCHED` — could not safely resolve; `reason` explains why.
 */
export const AmforiMatchResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('MATCHED'),
    companyId: z.string(),
    matchedVia: z.enum([
      'amfori_site_id',
      'external_reference',
      'hybrid_search',
    ]),
    consideredCandidates: z.array(ConsideredCandidateSchema).optional(),
  }),
  z.object({
    status: z.literal('SUGGESTED_OUT_OF_NETWORK'),
    companyId: z.string(),
    vectorSimilarity: z.number(),
    stringSimilarity: z.number(),
    matchedVia: z.literal('hybrid_search'),
    consideredCandidates: z.array(ConsideredCandidateSchema).optional(),
  }),
  z.object({
    status: z.literal('UNMATCHED'),
    reason: UnmatchedReasonSchema,
    consideredCandidates: z.array(ConsideredCandidateSchema).optional(),
  }),
]);

export type AmforiMatchResult = z.infer<typeof AmforiMatchResultSchema>;
